import moment from 'moment';
import React, {useEffect, useState} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Container, Typography, Paper, IconButton, Box, FormControl, InputLabel, Select, MenuItem, Tooltip, Button, Dialog, DialogActions, DialogTitle, DialogContent, Grid, List, ListItem, ListItemText } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import SourceIcon from '@mui/icons-material/Source';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import LoadingBox from '../../utils/LoadingBox';
import ClientContact from '../../utils/ClientContact';
import { getOperations, setFilter, checkIntegrity, resetIssues } from './OperationsSlice';
import { displayClientName, isEmpty } from '../../utils/Utils';

function Operations () {
    const { isLoading, ops, filter, issues } = useSelector((state) => state.ops);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [dataFetched, setDataFetched] = useState(false);
    const [showContact, setShowContact] = useState(false);
    const [client, setClient] = useState({});
    const [showSummary, setShowSummary] = useState(false);
    const [summaryDetails, setSummaryDetails] = useState({});
    const [showIssues, setShowIssues] = useState(false);

    useEffect(() => {
        if (Object.keys(ops).length === 0 && !dataFetched) {
            dispatch(getOperations());
            setDataFetched(true);
        }
    }, [ops, dataFetched, dispatch])

    useEffect(() => {
        if (!isEmpty(issues)) {
            setShowIssues(true);
        }
    }, [issues])

    const issuesClosed = () => {
        setShowIssues(false);
        dispatch(resetIssues());
    }

    const fillContactDetails = (params) => {
        const op = ops.find(op => op.id === params.row.id)
        setClient(op.booking.client);
        setShowContact(true);
    }

    const displaySummary = (params) => {
        const op = ops.find(op => op.id === params.row.id);
        setSummaryDetails(op);
        setShowSummary(true);
    }

    const filteredOps = filterOps(filter, ops);

    const columns = [
        {field: 'status', headerName: 'Status', renderCell: (params) => {
            const { value, row } = params;
            return (
                <div>
                    {value ? (
                        <Tooltip title='Action'>
                            <IconButton aria-label='Action' onClick={() => navigate(`/manage/ops/${row.id}/update`) }>
                                <WarningAmberIcon color='warning' />
                            </IconButton>
                        </Tooltip>
                    ) : (
                        <Tooltip title='Actioned'>
                            <IconButton aria-label='Actioned' onClick={() => navigate(`/manage/ops/${row.id}/update`) }>
                                <CheckCircleOutlineIcon color='success' />
                            </IconButton>
                        </Tooltip>
                    )}
                </div>
            )
        }},
        {field: 'op', headerName: 'Action'},
        {field: 'datetime', headerName: 'Date Time', flex:1},
        {field: 'location', headerName: 'Location', flex: 1},
        {field: 'vehicle', headerName: 'Vehicle', flex: 1},
        {field: 'client', headerName: 'Client', renderCell: (params) => {
            return (
                <Button variant='text' size='small' color='info' onClick={() => fillContactDetails(params)}>{params.value}</Button>
            )
        }},
        {field: 'insurance', headerName: 'Insurance'},
        {field: 'extras', headerName: 'Extras', flex:1},
        {field: 'paid', headerName: 'Paid', renderCell: (params) => {
            return (
                <Button variant='text' size='small' color='info' onClick={() => displaySummary(params)}>{params.value}</Button>
            )
        }},
        {field: 'booking', headerName: 'Booking', renderCell: (params) => {
            return (
                <Tooltip title='Edit booking'>
                    <IconButton aria-label='Edit booking' onClick={() => navigate(`/manage/bookings/${'operations'}/${params.value}/update`)}>
                        <SourceIcon />
                    </IconButton>
                </Tooltip>
            )
        }}
    ]

    const rows = filteredOps.map((op) => ({
        id: op.id,
        status: op.warning,
        op: op.operation_due,
        datetime: op.operation_due === 'Dispatch' ? moment(op.booking.pickup_datetime).format('D-MM-YY HH:mm') : moment(op.booking.dropoff_datetime).format('D-MM-YY HH:mm'),
        location: op.operation_due === 'Dispatch' 
            ? op.booking.delivery_address : op.booking.collection_address,
        vehicle: `${op.booking.vehicle.registration} ${op.booking.vehicle.model.vehicle_make} ${op.booking.vehicle.model.vehicle_model}`,
        client: displayClientName(op.booking.client),
        insurance: op.booking.insurance_waiver.code,
        extras: [...op.booking.extras.map((extra) => extra.code)].join(' | '),
        paid: op.percent_paid,
        booking: op.booking.reference
    }))

    return (
        <Container maxWidth='false'>
            <Typography variant='h3' align='center' gutterBottom>
                Operations
            </Typography>
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px'}}>
                <Button onClick={() => dispatch(checkIntegrity())} color='info' >Find issues</Button>
                <FormControl>
                    <InputLabel id='id-ops-filter'>Filter</InputLabel>
                    <Select
                        labelId='id-ops-filter'
                        id='ops-filter'
                        value={filter}
                        label='Today'
                        onChange={(e) => dispatch(setFilter(parseInt(e.target.value)))}
                    >
                        <MenuItem value={1}>Today</MenuItem>
                        <MenuItem value={2}>Tomorrow</MenuItem>
                        <MenuItem value={4}>Three days</MenuItem>
                        <MenuItem value={8}>Week</MenuItem>
                        <MenuItem value={31}>Month</MenuItem>
                        <MenuItem value={365}>All</MenuItem>
                    </Select>
                </FormControl>
            </Box>
            <Paper sx={{padding: 2}}>
                {isLoading || filteredOps.length === 0 ? (
                    <LoadingBox isLoading={isLoading} elements={ops} noneText={'No current ops'} />
                ) : (
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        getRowId={(row) => row.id || Math.random().toString(36).substring(2, 15)}
                        autoHeight
                        initialState={{
                            pagination: {
                                paginationModel: {page: 0, pageSize: 100},
                            },
                        }}
                        pageSizeOptions={[10, 25, 50, 100]}
                    />
                )}
            </Paper>
            <Dialog open={showContact} onClose={() => setShowContact(false)} aria-labelledby='alert-dialog-title' aria-describedby='alert-dialog-content'>
                <DialogTitle id='alert-dialog-title'>{'Customer contact'}</DialogTitle>
                <DialogContent>
                    <ClientContact client={client} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowContact(false)}>Close</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={showSummary} onClose={() => setShowSummary(false)} aria-labelledby='summary-dialog-title' aria-describedby='summary-dialog-content'>
                <DialogTitle id='summary-dialog-title'>{'Rental summary'}</DialogTitle>
                <DialogContent>
                    <BookingSummary op={summaryDetails} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowSummary(false)}>Close</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={showIssues} onClose={() => issuesClosed()} aria-labelledby='issues-dialog-title' aria-describedby='issues-dialog-content'>
                <DialogTitle id='issues-dialog-title'>Data integrity issues</DialogTitle>
                <DialogContent>
                    <IntegrityIssues issues={issues} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => issuesClosed()}>Close</Button>
                </DialogActions>
            </Dialog>
        </Container>
    )
}

export default Operations;


export const filterOps = (filter, ops) => {
    if (!ops.length) {
        return [];
    }
    if (filter === 365) {
        return ops;
    };
    const today = new Date();
    const cutoffDate = new Date(today);
    cutoffDate.setDate(today.getDate() + filter);
    cutoffDate.setHours(0, 0, 0, 0);
    return ops.filter((op) => {
        const pickup = new Date(op.booking.pickup_datetime);
        const dropoff = new Date(op.booking.dropoff_datetime);
        return (
            (op.booking.status === '1' && moment(pickup).isBefore(cutoffDate)) || 
            (op.booking.status === '2' && moment(dropoff).isBefore(cutoffDate))
        )
    }).sort((a,b) => {
        const dateA = new Date(a.booking.pickup_datetime);
        const dateB = new Date(b.booking.pickup_datetime);
        return dateA.getTime() - dateB.getTime();
    });
}

function BookingSummary ({op}) {
    const bookingSummary = op?.booking?.booking_summary;
    const keys = Object.keys(bookingSummary);

    return (
        <Box sx={{ width: '100%' }}>
            <Grid container alignItems='center' justifyContent='space-between' py={2}>
                <Grid item sx={{ textAlign: 'left'}}>
                    <Typography>Booking:    </Typography>
                </Grid>
                <Grid item sx={{ textAlign: 'right'}}>
                    <Typography>{op.booking.reference}</Typography>
                </Grid>
            </Grid>
            {keys.map((key, index) => {
                const item = bookingSummary[key];
                let displayedValue = item[0]

                if (index === 0) {
                    displayedValue = `Rental for ${item[0]} days`;
                }

                return (
                    <React.Fragment key={key}>
                        <Grid container alignItems='center' justifyContent='space-between'>
                            <Grid item sx={{ textAlign: 'left'}}>
                                <Typography  sx={{ marginRight: index === 0 ? 5 : 0}}>{displayedValue}     </Typography>
                            </Grid>
                            <Grid item sx={{ textAlign: 'right'}}>
                                <Typography>{item[1]}</Typography>
                            </Grid>
                        </Grid>
                    </React.Fragment>
                )
            })}
            <Grid container alignItems='center' justifyContent='space-between' py={2}>
                <Grid item xs={6} sx={{ textAlign: 'left'}}>
                    <Typography variant='subtitle2'>Amount unpaid:    </Typography>
                </Grid>
                <Grid item xs={6} sx={{ textAlign: 'right'}}>
                    <Typography variant='subtitle2'>{op.booking.invoice_currency} {Math.ceil(op.balance_due)}</Typography>
                </Grid>
                <Grid item xs={6} sx={{ textAlign: 'left'}}>
                    <Typography variant='subtitle2'>Payable online:    </Typography>
                </Grid>
                <Grid item xs={6} sx={{ textAlign: 'right'}}>
                    <Typography variant='subtitle2'>{op.totals.commission}</Typography>
                </Grid>
                <Grid item xs={6} sx={{ textAlign: 'left'}}>
                    <Typography variant='subtitle2'>Payable on pick-up:    </Typography>
                </Grid>
                <Grid item xs={6} sx={{ textAlign: 'right'}}>
                    <Typography variant='subtitle2'>{op.totals.balance_on_rental}</Typography>
                </Grid>
            </Grid>
        </Box>
    )
}


function IntegrityIssues ({issues}) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, padding: 2 }}>
            {issues.map((issue, index) => {
                const vehicle = Object.keys(issue)[0];
                const bookings = issue[vehicle];
                return (
                    <Box key={index} sx={{ border: '1px solid #ccc', borderRadius: 1, padding: 2 }}>
                        <Typography variant='h6' gutterBottom>{vehicle}</Typography>
                        <List>
                            {bookings.map((item, itemIndex) => (
                                <ListItem key={itemIndex}>
                                    <ListItemText 
                                        primary={`Booking ${item.reference}`}
                                        secondary={`Starts ${moment.utc(item.from).local().format('LLL')} - Ends ${moment.utc(item.to).local().format('LLL')}`}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )
            })}
        </Box>
    )
}